import React, { useCallback, useEffect, useState } from 'react'
import axios from 'axios'
import InvoiceListItem from './InvoiceListItem'
import { STATUS_OPTIONS } from './invoiceStatus'
import { isMockMode, mockList } from './mockData'

export default function InvoiceList({ url, selectedId, onSelect, refreshKey }) {
  const [statusFilter, setStatusFilter] = useState('')
  const [vendorFilter, setVendorFilter] = useState('')
  const [dateStart, setDateStart] = useState('')
  const [dateEnd, setDateEnd] = useState('')
  const [invoices, setInvoices] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [vendors, setVendors] = useState([])
  const limit = 25

  const fetchInvoices = useCallback(() => {
    if (!isMockMode() && !url) return
    setLoading(true)
    const params = { page, limit }
    if (statusFilter) params.status = statusFilter
    if (vendorFilter) params.vendor = vendorFilter
    if (dateStart) params.dateStart = dateStart
    if (dateEnd) params.dateEnd = dateEnd

    const promise = isMockMode()
      ? mockList(params).then(data => ({ data }))
      : axios.get(`${url}/api/subcontractor-invoices`, { params })

    promise
      .then(res => {
        setInvoices(res.data.invoices || [])
        setTotal(res.data.total || 0)
        // collect unique vendors for dropdown
        const seen = new Set()
        const vlist = []
        ;(res.data.invoices || []).forEach(inv => {
          if (inv.vendor?._id && !seen.has(inv.vendor._id)) {
            seen.add(inv.vendor._id)
            vlist.push({ _id: inv.vendor._id, name: inv.vendor.name })
          }
        })
        setVendors(prev => {
          const prevIds = new Set(prev.map(v => v._id))
          const merged = [...prev]
          vlist.forEach(v => { if (!prevIds.has(v._id)) merged.push(v) })
          return merged
        })
      })
      .catch(err => console.error('[InvoiceList] fetch error', err))
      .finally(() => setLoading(false))
  }, [url, page, statusFilter, vendorFilter, dateStart, dateEnd, refreshKey])

  useEffect(() => {
    setPage(1)
  }, [statusFilter, vendorFilter, dateStart, dateEnd])

  useEffect(() => {
    fetchInvoices()
  }, [fetchInvoices])

  const totalPages = Math.max(1, Math.ceil(total / limit))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Filter bar */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--line-1, #e0e8eb)', background: 'var(--bg-panel, #fff)' }}>
        {/* Status chips */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
          {STATUS_OPTIONS.map(tab => (
            <button
              key={tab.value}
              onClick={() => setStatusFilter(tab.value)}
              style={{
                padding: '3px 12px',
                borderRadius: 99,
                border: '1px solid',
                borderColor: statusFilter === tab.value ? 'var(--brand, #558b6e)' : 'var(--line-2, #cfd9dc)',
                background: statusFilter === tab.value ? 'var(--brand-tint, #e8f5e9)' : 'var(--bg-panel, #fff)',
                color: statusFilter === tab.value ? 'var(--brand-deep, #2e7d32)' : 'var(--ink-3, #607d8b)',
                fontSize: '12px',
                fontWeight: 550,
                cursor: 'pointer',
                fontFamily: 'var(--f-sans, inherit)',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {/* Vendor + date */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={vendorFilter}
            onChange={e => setVendorFilter(e.target.value)}
            style={{
              height: 28, padding: '0 8px', borderRadius: 5,
              border: '1px solid var(--line-2, #cfd9dc)',
              background: 'var(--bg-panel, #fff)',
              fontSize: '12.5px', color: 'var(--ink, #263238)',
              fontFamily: 'var(--f-sans, inherit)',
              cursor: 'pointer',
            }}
          >
            <option value="">All vendors</option>
            {vendors.map(v => (
              <option key={v._id} value={v._id}>{v.name}</option>
            ))}
          </select>
          <input
            type="date"
            value={dateStart}
            onChange={e => setDateStart(e.target.value)}
            title="From date"
            style={{
              height: 28, padding: '0 8px', borderRadius: 5,
              border: '1px solid var(--line-2, #cfd9dc)',
              background: 'var(--bg-panel, #fff)',
              fontSize: '12.5px', color: 'var(--ink, #263238)',
              fontFamily: 'var(--f-sans, inherit)',
            }}
          />
          <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>–</span>
          <input
            type="date"
            value={dateEnd}
            onChange={e => setDateEnd(e.target.value)}
            title="To date"
            style={{
              height: 28, padding: '0 8px', borderRadius: 5,
              border: '1px solid var(--line-2, #cfd9dc)',
              background: 'var(--bg-panel, #fff)',
              fontSize: '12.5px', color: 'var(--ink, #263238)',
              fontFamily: 'var(--f-sans, inherit)',
            }}
          />
          {(dateStart || dateEnd) && (
            <button
              onClick={() => { setDateStart(''); setDateEnd('') }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--ink-3)', padding: '0 4px' }}
            >
              Clear dates
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loading && (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
            Loading…
          </div>
        )}
        {!loading && invoices.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
            No invoices found.
          </div>
        )}
        {!loading && invoices.length > 0 && (
          <table className="hmi-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Vendor / Site</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => (
                <InvoiceListItem
                  key={inv._id}
                  invoice={inv}
                  selected={selectedId === inv._id}
                  onClick={() => onSelect(inv._id)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '8px 16px', borderTop: '1px solid var(--line-1, #e0e8eb)',
          background: 'var(--bg-panel, #fff)', fontSize: 12.5,
          color: 'var(--ink-3)', justifyContent: 'flex-end',
        }}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="hmi-btn hmi-btn-outline hmi-btn-sm"
          >
            Prev
          </button>
          <span>Page {page} / {totalPages} ({total} total)</span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="hmi-btn hmi-btn-outline hmi-btn-sm"
          >
            Next
          </button>
        </div>
      )}
    </div>
  )
}
